// NextScene — shared login-prompt context.
//
// Any page/component can gate a restricted action with:
//
//   const { requireAuth, open } = useLoginPrompt();
//   const handleLike = () => requireAuth(() => doLike());
//
// `requireAuth(fn)` — if the user is signed in, runs `fn` immediately;
// otherwise opens the global cinematic Login modal.
// `open()` — opens the Login modal directly (used by the top-nav button).
import { createContext, useCallback, useContext, useState } from "react";
import { useAuth } from "@/lib/auth";

const LoginPromptCtx = createContext(null);

export function LoginPromptProvider({ children }) {
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);

  return (
    <LoginPromptCtx.Provider value={{ open, setOpen, openModal, closeModal }}>
      {children}
    </LoginPromptCtx.Provider>
  );
}

/**
 * Preferred hook. Returns:
 *   { open, openModal, closeModal, requireAuth }
 * `requireAuth(fn)` gates an action behind the modal.
 */
export function useLoginPrompt() {
  const ctx = useContext(LoginPromptCtx);
  const auth = useAuth() || {};
  if (!ctx) throw new Error("useLoginPrompt must be used inside <LoginPromptProvider>");

  const requireAuth = useCallback((fn) => {
    if (auth.user) return typeof fn === "function" ? fn() : undefined;
    ctx.openModal();
    return undefined;
  }, [auth.user, ctx]);

  return { ...ctx, requireAuth };
}
